import { useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { getConnection, startConnection } from '../services/signalr'
import { hasAuthentication } from '../services/api'
import type {
  EtlJobStartedPayload,
  EtlJobCompletedPayload,
} from '../types/api'

export interface EtlCompletedPayload {
  entityName: string
  module: string | null
  extractedCount: number
  loadedCount: number
  duration: number
  wasReset: boolean
  timestamp: string
}

export interface EtlFailedPayload {
  entityName: string
  module: string | null
  failedPhase: string
  errorMessage: string
  duration: number
  timestamp: string
}

export interface OrchestratorCompletedPayload {
  orchestratorName: string
  module: string
  entitiesProcessed: number
  successCount: number
  failureCount: number
  totalRecordsLoaded: number
  duration: number
  wasReset: boolean
  failedEntities: string[]
  timestamp: string
}

export interface DataQualityAlertPayload {
  entityName: string
  issueType: string
  severity: string
  description: string
  affectedRecords: number | null
  timestamp: string
}

/**
 * What an ETL completion makes out of date, as query-key PREFIXES: React Query matches a key by its
 * leading elements, so ['observatory','etlRuns'] reaches every filtered list the Explorer holds and
 * ['observatory','etlRun'] every single run, without either reaching the other.
 *
 * These replace four keys (['activityLog'], ['jobSummary'], ['signalMetrics'], ['entityCounts']) that no
 * query used, so a completion used to refresh nothing on screen. Keep this list to keys a hook really
 * mounts: an entry nothing uses fails silently, which is how the four dead ones survived.
 */
export const DASHBOARD_QUERY_PREFIXES: readonly (readonly string[])[] = [
  ['observatory', 'etlRuns'],
  ['observatory', 'etlRun'],
  ['observatory', 'executions'],
  ['observatory', 'execution'],
  ['observatory', 'liveOperations'],
  ['observatory', 'throughput'],
  ['observatory', 'topEntities'],
  ['observatory', 'apiHealth'],
]

/** Marks every dashboard query stale and refetches the mounted ones. */
export function invalidateDashboardQueries(queryClient: QueryClient): void {
  for (const queryKey of DASHBOARD_QUERY_PREFIXES) {
    void queryClient.invalidateQueries({ queryKey })
  }
}

export function useSignalR() {
  const queryClient = useQueryClient()
  const mountedRef = useRef(true)

  const invalidateDashboard = useCallback(() => {
    invalidateDashboardQueries(queryClient)
  }, [queryClient])

  const invalidateEtlActivity = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['etlActivity'] })
  }, [queryClient])

  useEffect(() => {
    mountedRef.current = true

    if (!hasAuthentication()) {
      return
    }

    const connection = getConnection()

    // Store handler references so cleanup only removes these specific handlers
    // (not handlers registered by other hooks like useEtlActivity)
    const onEtlCompleted = (data: EtlCompletedPayload) => {
      if (!mountedRef.current) return

      notifications.show({
        title: 'ETL Completed',
        message: `${data.entityName} loaded ${data.loadedCount} records in ${data.duration.toFixed(1)}s`,
        color: 'green',
        autoClose: 5000,
      })

      invalidateDashboard()
      invalidateEtlActivity()
    }

    const onEtlFailed = (data: EtlFailedPayload) => {
      if (!mountedRef.current) return

      notifications.show({
        title: 'ETL Failed',
        message: `${data.entityName} failed during ${data.failedPhase}: ${data.errorMessage}`,
        color: 'red',
        autoClose: 10000,
      })

      invalidateDashboard()
      invalidateEtlActivity()
    }

    const onOrchestratorCompleted = (data: OrchestratorCompletedPayload) => {
      if (!mountedRef.current) return

      const hasFailures = data.failureCount > 0
      notifications.show({
        title: hasFailures ? 'ETL Orchestrator Completed with Errors' : 'ETL Orchestrator Completed',
        message: `${data.module}: ${data.successCount}/${data.entitiesProcessed} entities, ${data.totalRecordsLoaded.toLocaleString()} records in ${data.duration.toFixed(1)}s`,
        color: hasFailures ? 'orange' : 'blue',
        autoClose: 8000,
      })

      invalidateDashboard()
      invalidateEtlActivity()
    }

    const onDataQualityAlert = (data: DataQualityAlertPayload) => {
      if (!mountedRef.current) return

      const colorMap: Record<string, string> = {
        Info: 'blue',
        Warning: 'yellow',
        Error: 'red',
        Critical: 'red',
      }

      notifications.show({
        title: `Data Quality: ${data.issueType}`,
        message: `${data.entityName}: ${data.description}`,
        color: colorMap[data.severity] || 'yellow',
        autoClose: data.severity === 'Critical' ? false : 8000,
      })
    }

    const onJobStarted = (data: EtlJobStartedPayload) => {
      if (!mountedRef.current) return

      notifications.show({
        title: 'ETL Job Started',
        message: `${data.jobType} job started${data.wasReset ? ' (full reset)' : ''} - ${data.totalModules} modules`,
        color: 'blue',
        autoClose: 5000,
      })

      invalidateEtlActivity()
    }

    const onJobCompleted = (data: EtlJobCompletedPayload) => {
      if (!mountedRef.current) return

      const hasFailures = data.failureCount > 0
      notifications.show({
        title: hasFailures ? 'ETL Job Completed with Errors' : 'ETL Job Completed',
        message: `${data.jobType}: ${data.totalEntitiesProcessed} entities, ${data.totalRecordsLoaded.toLocaleString()} records in ${data.duration.toFixed(1)}s`,
        color: hasFailures ? 'orange' : 'green',
        autoClose: 8000,
      })

      invalidateDashboard()
      invalidateEtlActivity()
    }

    // High-frequency events: acknowledged, and nothing more. They used to invalidate the activity
    // history, which is written only when an entity or a run FINISHES (EtlRunTelemetry), so each
    // progress tick (up to one a second per entity) refetched an unchanged page and, because an
    // invalidation cancels the fetch in flight, could keep it from ever landing. They stay subscribed
    // because this hook is mounted app-wide: with no handler at all, the SignalR client logs "No client
    // method with the name ... found" on every tick on every other page. useEtlActivity handles them
    // on Live Activity, enriching its banner and re-asking the live query, throttled.
    const acknowledge = () => {}

    connection.on('EtlCompleted', onEtlCompleted)
    connection.on('EtlFailed', onEtlFailed)
    connection.on('OrchestratorCompleted', onOrchestratorCompleted)
    connection.on('DataQualityAlert', onDataQualityAlert)
    connection.on('EtlJobStarted', onJobStarted)
    connection.on('EtlJobCompleted', onJobCompleted)
    connection.on('OrchestratorStarted', acknowledge)
    connection.on('EntityEtlStarted', acknowledge)
    connection.on('EtlProgress', acknowledge)

    startConnection()

    return () => {
      mountedRef.current = false
      connection.off('EtlCompleted', onEtlCompleted)
      connection.off('EtlFailed', onEtlFailed)
      connection.off('OrchestratorCompleted', onOrchestratorCompleted)
      connection.off('DataQualityAlert', onDataQualityAlert)
      connection.off('EtlJobStarted', onJobStarted)
      connection.off('EtlJobCompleted', onJobCompleted)
      connection.off('OrchestratorStarted', acknowledge)
      connection.off('EntityEtlStarted', acknowledge)
      connection.off('EtlProgress', acknowledge)
    }
  }, [invalidateDashboard, invalidateEtlActivity])
}
